import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Language } from '../types/kundali';
import {
  Compass,
  Clock,
  FileText,
  BookOpen,
  BarChart3,
  Layers,
  Activity,
  Star,
  Triangle,
  Award,
  Calendar,
  Gem,
  HelpCircle,
  Shield,
  Sparkles,
  PieChart,
  User,
  Users,
  Hash,
  Hand,
  Home,
  Table,
  Cpu,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  Printer,
  Zap
} from 'lucide-react';

export interface FeatureNavigationStripProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  viewMode: 'simple' | 'astrologer';
  lang: Language;
}

export interface NavFeatureItem {
  id: string;
  labelEn: string;
  labelHi: string;
  icon: React.ComponentType<{ className?: string }>;
  descriptionEn?: string;
  descriptionHi?: string;
}

export const ADVANCED_ASTRO_FEATURES: NavFeatureItem[] = [
  {
    id: 'enterprise-studio',
    labelEn: 'Vedic Destiny Studio',
    labelHi: 'वैदिक डेस्टिनी स्टूडियो',
    icon: Compass,
    descriptionEn: 'Adaptive Milestone Pipeline, 4 Purushartha Spheres, 100+ Deep Inquiries & 5-Layer Karmic Engine',
    descriptionHi: 'अनुकूली काल-चक्र मील के पत्थर, ४ पुरुषार्थ क्षेत्र, १००+ गहन जीवन जिज्ञासाएं व ५-स्तरीय कर्म फलादेश'
  },
  {
    id: 'workbench',
    labelEn: 'V6.1 Kundali Workbench',
    labelHi: 'V6.1 कुंडली वर्कबेंच',
    icon: Sparkles,
    descriptionEn: 'Deterministic V6.1 Ephemeris, Vargas, Shadbala, Ashtakavarga & AI Gateway V2',
    descriptionHi: 'V6.1 पंचांग, वर्ग, षडबल, अष्टकवर्ग व एआई गेटवे वी२'
  },
  {
    id: 'advanced-life-analysis',
    labelEn: 'Advanced Life Analysis',
    labelHi: 'विस्तृत जीवन विश्लेषण',
    icon: Compass,
    descriptionEn: '13 Life Themes, Deterministic Evidence Plans & Phala-Bhuta Timing Windows',
    descriptionHi: '१३ जीवन क्षेत्र, प्रमाण योजना व सटीक फल-प्रदाता काल'
  },
  {
    id: 'spirituality',
    labelEn: 'Spirituality',
    labelHi: 'अध्यात्म एवं साधना',
    icon: Sparkles,
    descriptionEn: '8-Card Seeker Presentation: Nature, Path, Practice, Inner Growth, Dharma/Moksha, Challenges, Journey & Current Activation',
    descriptionHi: '८-कार्ड जिज्ञासु प्रस्तुति: स्वभाव, साधना मार्ग, अभ्यास, आंतरिक विकास, धर्म/मोक्ष, चुनौतियां, जीवन यात्रा व सक्रियता'
  },
  {
    id: 'astrologer-dossier',
    labelEn: "Astrologer's Dossier",
    labelHi: 'ज्योतिषी निदान डॉसियर',
    icon: Compass,
    descriptionEn: '4-Phase Classical Protocol: Three Pillars, D9 Shifts, Chara Karakas & Double Transits',
    descriptionHi: '४-चरण शास्त्रीय निदान: त्रि-स्तम्भ, नवांश पुष्टि, चर कारक व संयुक्त गोचर'
  },
  {
    id: 'charts',
    labelEn: 'Charts / D1–D60',
    labelHi: 'वर्ग कुंडलियां (D1-D60)',
    icon: Compass,
    descriptionEn: 'Divisional chart system (D1 to D60) with lagna and house overlays',
    descriptionHi: 'षोडशवर्ग व D1-D60 चक्र'
  },
  {
    id: 'dashas',
    labelEn: 'Dasha Evolution',
    labelHi: 'दशा विकास टाइमलाइन',
    icon: Clock,
    descriptionEn: 'Vimshottari Mahadasha, Antardasha & Pratyantardasha periods',
    descriptionHi: 'विंशोत्तरी महादशा व अंतर्दशा'
  },
  {
    id: 'doshas',
    labelEn: 'Yogas & Doshas',
    labelHi: 'योग एवं दोष विश्लेषण',
    icon: FileText,
    descriptionEn: 'Mangal, Kaal Sarp, Sade Sati, Raj Yogas & planetary combinations',
    descriptionHi: 'मंगल, कालसर्प, साढ़ेसाती व राजयोग'
  },
  {
    id: 'ashtakvarga',
    labelEn: 'Ashtakavarga',
    labelHi: 'अष्टकवर्ग चक्र',
    icon: BookOpen,
    descriptionEn: 'Sarvashtakavarga and Bhinnashtakavarga bindu distribution',
    descriptionHi: 'सर्वाष्टकवर्ग व भिन्नाष्टकवर्ग बिंदु'
  },
  {
    id: 'shadbala',
    labelEn: 'Shadbala & Dignities',
    labelHi: 'षडबल व ग्रह बल',
    icon: BarChart3,
    descriptionEn: 'Six-fold planetary strength calculation & positional dignity',
    descriptionHi: 'ग्रहों की षडबल शक्ति व अनुपात'
  },
  {
    id: 'kp',
    labelEn: 'KP System',
    labelHi: 'के.पी. ज्योतिष प्रणाली',
    icon: Layers,
    descriptionEn: 'Krishnamurti Padhdhati cusps, sub-lords & ruling planets',
    descriptionHi: 'कृष्णमूर्ति पद्धति भाव व उप-स्वामी'
  },
  {
    id: 'specialized',
    labelEn: 'Specialized Diagnostics',
    labelHi: 'विशिष्ट शास्त्रीय निदान',
    icon: Star,
    descriptionEn: 'Navtara, Trines, Karakas, Dignity, Horary, Rectification & Astrologer Core',
    descriptionHi: 'नवतारा, त्रिकोण, कारक, प्रश्न व शोधन'
  },
  {
    id: 'navtara',
    labelEn: 'Navtara Chakra',
    labelHi: 'नवतारा चक्र',
    icon: Star,
    descriptionEn: '27 Nakshatra 9-Tara auspiciousness matrix from Janma Nakshatra',
    descriptionHi: 'जन्मतारा से ९-तारा चक्र'
  },
  {
    id: 'dignity',
    labelEn: 'Planetary Dignity',
    labelHi: 'ग्रह बल व गरिमा',
    icon: Award,
    descriptionEn: 'Exaltation, Moolatrikona, Own sign, Friendly, Debilitation and combustion status',
    descriptionHi: 'उच्च, मूलत्रिकोण, स्वक्षेत्र व नीच स्थिति'
  },
  {
    id: 'karka',
    labelEn: 'Significators / Karka',
    labelHi: 'कारक (आत्मकारक आदि)',
    icon: Award,
    descriptionEn: 'Jaimini Chara Karakas (Atmakaraka, Amatyakaraka, etc.) & Sthira Karakas',
    descriptionHi: 'जैमिनी चर कारक व स्थिर कारक'
  },
  {
    id: 'trines',
    labelEn: 'Trines & Arishta',
    labelHi: 'त्रिकोण व अरिष्ट',
    icon: Triangle,
    descriptionEn: 'Trikona houses (1, 5, 9), Kendra-Trikona Raj Yogas and Arishta patterns',
    descriptionHi: 'त्रिकोण भाव व अरिष्ट योग'
  },
  {
    id: 'dashaChart',
    labelEn: 'Dasha Chart',
    labelHi: 'दशा प्रवेश चक्र',
    icon: PieChart,
    descriptionEn: 'Planetary positions mapped to currently running Mahadasha & Antardasha lords',
    descriptionHi: 'दशा स्वामी आधारित कुंडली चक्र'
  },
  {
    id: 'muhurta',
    labelEn: 'Muhurta Advisor',
    labelHi: 'मुहूर्त शोधन',
    icon: Calendar,
    descriptionEn: 'Classical electional astrology for travel, business, ceremony and purchases',
    descriptionHi: 'शुभ मुहूर्त व वर्जित समय'
  },
  {
    id: 'panchang',
    labelEn: 'Daily Panchang',
    labelHi: 'दैनिक पंचांग व मुहूर्त',
    icon: Calendar,
    descriptionEn: 'Tithi, Vara, Nakshatra, Yoga, Karana, Rahu Kaal and auspicious timings',
    descriptionHi: 'तिथि, वार, नक्षत्र, योग, करण व राहुकाल'
  },
  {
    id: 'full-panchang',
    labelEn: 'Full Panchang',
    labelHi: 'सम्पूर्ण पंचांग',
    icon: Calendar,
    descriptionEn: 'Complete ephemeris, 60-year Samvatsara, Ritu, Ayana, directional shool & planetary details',
    descriptionHi: 'विस्तृत पंचांग, संवत्सर, ऋतु, अयन, दिश शूल व ग्रह स्पष्ट'
  },
  {
    id: 'publication-dossier',
    labelEn: 'Publication Dossier',
    labelHi: 'प्रकाशन डॉसियर एवं प्रिंट',
    icon: Printer,
    descriptionEn: 'Interactive Brihat Janmapatrika publication dossier, content-aware pagination, and in-app HTML print preview tool.',
    descriptionHi: 'वृहत् जन्मपत्रिका प्रकाशन डॉसियर, पन्ने का लेआउट एवं इन-ऐप एचटीएमएल प्रिंट प्रिव्यू टूल'
  },
  {
    id: 'shubha-essentials',
    labelEn: 'Shubha Essentials & Remedies',
    labelHi: 'शुभ तत्व व उपाय',
    icon: Sparkles,
    descriptionEn: 'Unified auspicious timing (Muhurta), personal indicators (Colors, Numbers, Navtara) & Vedic remedies',
    descriptionHi: 'शुभ मुहूर्त, व्यक्तिगत तत्व (रंग, अंक, नवतारा चक्र) व संपूर्ण वैदिक उपाय'
  },
  {
    id: 'prashna',
    labelEn: 'Prashna Horary',
    labelHi: 'प्रश्न शास्त्र',
    icon: HelpCircle,
    descriptionEn: 'Instant horary question casting based on moment of inquiry',
    descriptionHi: 'तात्कालिक प्रश्न कुंडली गणना'
  },
  {
    id: 'rectification',
    labelEn: 'Time Rectification',
    labelHi: 'जन्म समय शोधन',
    icon: Clock,
    descriptionEn: 'Tattva, Pranapada, Kunda, and life-event based birth time rectification tools',
    descriptionHi: 'तत्व व कुण्ड आधारित समय शोधन'
  },
  {
    id: 'planets',
    labelEn: 'Planetary Table',
    labelHi: 'ग्रह स्पष्ट तालिका',
    icon: Table,
    descriptionEn: 'Precise planetary longitudes, speeds, retrograde state, nakshatra padas',
    descriptionHi: 'ग्रहों की गति, नक्षत्र पद व स्पष्ट मान'
  },
  {
    id: 'core-view',
    labelEn: 'Astrologer Core View',
    labelHi: 'कोर ज्योतिषी व्यू',
    icon: Shield,
    descriptionEn: 'High-density multi-pane chart analysis workspace for professional consulting',
    descriptionHi: 'व्यावसायिक बहु-चक्र विश्लेषण'
  },
  {
    id: 'vedic-engine',
    labelEn: 'Vedic Engine',
    labelHi: 'वैदिक गणना इंजन',
    icon: Cpu,
    descriptionEn: 'Algorithmic astronomical planetary engine audit and raw calculation verification',
    descriptionHi: 'खगोलीय गणना व सत्यापन इंजन'
  },
  {
    id: 'gochar',
    labelEn: 'Gochar & Transits',
    labelHi: 'गोचर व साढ़ेसाती',
    icon: Activity,
    descriptionEn: 'Live planetary transits overlaid on natal houses with Ashtakavarga transit scores',
    descriptionHi: 'वर्तमान गोचर व तात्कालिक ग्रह स्थिति'
  }
];

export const BASIC_FEATURES: NavFeatureItem[] = [
  {
    id: 'enterprise-studio',
    labelEn: 'Vedic Destiny Studio',
    labelHi: 'वैदिक डेस्टिनी स्टूडियो',
    icon: Compass,
    descriptionEn: 'Adaptive Milestone Pipeline, 4 Purushartha Spheres, 100+ Deep Inquiries & 5-Layer Karmic Engine',
    descriptionHi: 'अनुकूली काल-चक्र मील के पत्थर, ४ पुरुषार्थ क्षेत्र, १००+ गहन जीवन जिज्ञासाएं व ५-स्तरीय कर्म फलादेश'
  },
  {
    id: 'advanced-life-analysis',
    labelEn: 'Life Questions',
    labelHi: 'जीवन प्रश्न',
    icon: Compass,
    descriptionEn: 'Explore what your chart suggests about the areas that matter most to you.',
    descriptionHi: 'जानिए आपकी कुंडली जीवन के महत्वपूर्ण क्षेत्रों के बारे में क्या संकेत देती है।'
  },
  {
    id: 'spirituality',
    labelEn: 'Spirituality',
    labelHi: 'अध्यात्म एवं साधना',
    icon: Sparkles,
    descriptionEn: '8-Card Seeker Presentation: Nature, Path, Practice, Inner Growth, Dharma/Moksha, Challenges, Journey & Current Activation',
    descriptionHi: '८-कार्ड जिज्ञासु प्रस्तुति: स्वभाव, साधना मार्ग, अभ्यास, आंतरिक विकास, धर्म/मोक्ष, चुनौतियां, जीवन यात्रा व सक्रियता'
  },
  {
    id: 'analytics',
    labelEn: 'Personality & Life Profile',
    labelHi: 'व्यक्तित्व एवं जीवन प्रोफाइल',
    icon: User,
    descriptionEn: 'Holistic life overview across health, career, wealth and spirituality'
  },
  {
    id: 'simple-reading',
    labelEn: 'Summary Reading',
    labelHi: 'संक्षिप्त जीवन फल',
    icon: Sparkles,
    descriptionEn: 'Plain-language interpretation of your birth chart strengths and forecasts'
  },
  {
    id: 'ai-consult',
    labelEn: 'AI Jyotish Acharya',
    labelHi: 'एआई ज्योतिषाचार्य',
    icon: Sparkles,
    descriptionEn: 'Interactive AI-powered classical Jyotish consultation and answers'
  },
  {
    id: 'panchang',
    labelEn: 'Daily Panchang & Muhurta',
    labelHi: 'दैनिक पंचांग व मुहूर्त',
    icon: Calendar,
    descriptionEn: 'Tithi, Vara, Nakshatra, Yoga, Karana, Rahu Kaal and auspicious timings'
  },
  {
    id: 'shubha-essentials',
    labelEn: 'Shubha Essentials & Remedies',
    labelHi: 'शुभ तत्व व उपाय',
    icon: Sparkles,
    descriptionEn: 'Personal auspicious colors, numbers, directions, Navtara, Muhurta & Vedic remedies',
    descriptionHi: 'शुभ रंग, अंक, दिशा, नवतारा चक्र, मुहूर्त व वैदिक उपाय'
  },
  {
    id: 'matching',
    labelEn: 'Kundali Matching',
    labelHi: 'कुंडली मिलान (गुण मिलान)',
    icon: Users,
    descriptionEn: 'Ashtakoota 36-guna Milan, Manglik Dosha matching and compatibility'
  },
  {
    id: 'numerology',
    labelEn: 'Numerology & Lucky #',
    labelHi: 'अंक ज्योतिष (शुभ अंक/रंग)',
    icon: Hash,
    descriptionEn: 'Moolank, Bhagyank, Name number and favorable dates/colors',
    descriptionHi: 'मूलांक, भाग्यांक, शुभ अंक, रंग व नामांक'
  },
  {
    id: 'palmistry',
    labelEn: 'Palm Study',
    labelHi: 'हस्तरेखा अध्ययन',
    icon: Hand,
    descriptionEn: 'Palmistry line analysis and planetary mount interpretations'
  },
  {
    id: 'vastu',
    labelEn: 'Vastu Shastra',
    labelHi: 'वास्तु शास्त्र',
    icon: Home,
    descriptionEn: 'Directional energies, residential balance and Vastu remedies'
  }
];

export const FeatureNavigationStrip: React.FC<FeatureNavigationStripProps> = ({
  activeTab,
  setActiveTab,
  viewMode,
  lang
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const activeBtnRef = useRef<HTMLButtonElement | null>(null);

  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const isBasic = viewMode === 'simple';
  const features = isBasic ? BASIC_FEATURES : ADVANCED_ASTRO_FEATURES;
  const isHi = lang === 'hi';

  // Check scroll positions to display left/right fade cues and affordances
  const updateScrollState = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;

    setShowLeftFade(scrollLeft > 6);
    setShowRightFade(maxScroll > 6 && scrollLeft < maxScroll - 6);
  }, []);

  // Update scroll state on resize and mount
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    updateScrollState();
    const handleResize = () => updateScrollState();
    window.addEventListener('resize', handleResize);

    const observer = new ResizeObserver(() => updateScrollState());
    observer.observe(el);

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, [updateScrollState, features]);

  // Smooth scroll active button into view whenever activeTab changes
  useEffect(() => {
    if (activeBtnRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const btn = activeBtnRef.current;

      const containerRect = container.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();

      // Check if button is outside or partially outside the visible viewport
      const isPartiallyLeft = btnRect.left < containerRect.left + 16;
      const isPartiallyRight = btnRect.right > containerRect.right - 16;

      if (isPartiallyLeft || isPartiallyRight) {
        btn.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest'
        });
      }
      // Recheck scroll state after animation
      setTimeout(updateScrollState, 350);
    }
  }, [activeTab, updateScrollState]);

  // Horizontal mouse wheel helper (enables vertical mouse wheel to scroll horizontally when hovered over strip)
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      // If user is holding shift, browser handles horizontal scroll natively.
      // If deltaY exists and deltaX is negligible, translate deltaY to horizontal scroll.
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        const canScrollLeft = el.scrollLeft > 0 && e.deltaY < 0;
        const canScrollRight = el.scrollLeft < el.scrollWidth - el.clientWidth && e.deltaY > 0;

        if (canScrollLeft || canScrollRight) {
          e.preventDefault();
          el.scrollBy({
            left: e.deltaY * 1.2,
            behavior: 'auto'
          });
          updateScrollState();
        }
      }
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', handleWheel);
    };
  }, [updateScrollState]);

  // Scroll by step handlers for Left/Right chevron affordance buttons
  const scrollStep = (direction: 'left' | 'right') => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const scrollAmount = Math.max(220, Math.floor(el.clientWidth * 0.65));
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
    setTimeout(updateScrollState, 300);
  };

  return (
    <nav
      id="feature-navigation-rail"
      aria-label={isBasic ? 'Basic Astrological Features' : 'Advanced Astrological Classical Modules'}
      className="relative bg-astro-bg border border-astro-border rounded-2xl p-1.5 sm:p-2 shadow-2xs w-full max-w-full overflow-hidden select-none"
    >
      {/* Visual Left Fade Gradient Indicator */}
      {showLeftFade && (
        <div className="absolute left-0 top-0 bottom-0 w-8 sm:w-12 bg-gradient-to-r from-astro-bg via-astro-bg/85 to-transparent z-10 pointer-events-none transition-opacity duration-200" />
      )}

      {/* Visual Right Fade Gradient Indicator */}
      {showRightFade && (
        <div className="absolute right-0 top-0 bottom-0 w-8 sm:w-12 bg-gradient-to-l from-astro-bg via-astro-bg/85 to-transparent z-10 pointer-events-none transition-opacity duration-200" />
      )}

      {/* Left Scroll Button Affordance (appears when scrolled right) */}
      {showLeftFade && (
        <button
          type="button"
          onClick={() => scrollStep('left')}
          className="absolute left-1.5 top-1/2 -translate-y-1/2 z-20 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-astro-bg border border-astro-border text-astro-primary shadow-md flex items-center justify-center hover:bg-astro-card active:scale-95 transition-all cursor-pointer"
          title={isHi ? 'बाएं स्क्रॉल करें' : 'Scroll Left'}
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-4 h-4 sm:w-4.5 sm:h-4.5" />
        </button>
      )}

      {/* Right Scroll Button Affordance (appears when more content exists to the right) */}
      {showRightFade && (
        <button
          type="button"
          onClick={() => scrollStep('right')}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 z-20 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-astro-bg border border-astro-border text-astro-primary shadow-md flex items-center justify-center hover:bg-astro-card active:scale-95 transition-all cursor-pointer"
          title={isHi ? 'दाएं स्क्रॉल करें' : 'Scroll Right'}
          aria-label="Scroll right"
        >
          <ChevronRight className="w-4 h-4 sm:w-4.5 sm:h-4.5" />
        </button>
      )}

      {/* The Horizontally Scrollable Feature Strip */}
      <div
        ref={scrollContainerRef}
        onScroll={updateScrollState}
        className="w-full overflow-x-auto custom-horizontal-scrollbar pb-1.5 py-1"
        style={{
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth'
        }}
      >
        <div
          className="flex items-center gap-2 min-w-max px-1"
          role="tablist"
          aria-label={isBasic ? 'Basic Astrological Features' : 'Advanced Astrological Classical Modules'}
        >
          {features.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            const label = isHi ? item.labelHi : item.labelEn;
            const description = isHi ? item.descriptionHi : item.descriptionEn;

            return (
              <button
                key={item.id}
                id={`nav-tab-${item.id}`}
                ref={isActive ? activeBtnRef : null}
                type="button"
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                aria-current={isActive ? 'page' : undefined}
                onClick={() => setActiveTab(item.id)}
                title={description || label}
                className={`shrink-0 whitespace-nowrap flex items-center gap-1.5 sm:gap-2 px-3 sm:px-3.5 py-1.5 rounded-xl border text-xs font-semibold shadow-xs transition-all duration-150 cursor-pointer min-h-[36px] focus:outline-none focus-visible:ring-2 focus-visible:ring-astro-primary ${
                  isActive
                    ? 'bg-astro-primary text-white border-astro-primary shadow-xs scale-[1.01]'
                    : 'bg-astro-bg text-astro-secondary-text border-astro-border hover:text-astro-primary hover:bg-astro-card'
                }`}
              >
                <Icon
                  className={`w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 transition-colors ${
                    isActive ? 'text-astro-accent' : 'text-astro-secondary-text'
                  }`}
                />
                <span className="font-semibold">{label}</span>
                {isActive && (
                  <span className="w-1.5 h-1.5 rounded-full bg-astro-accent shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};
